import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type ClientType = 'Web' | 'Electron' | 'WPF' | 'Unknown' | 'Portal';

interface AuthState {
  token: string | null;
  tenantSlug: string | null;
  sessionId: string | null;
  clientType: ClientType;
  // Staff session — set after WHO'S WORKING? login; null for solo shops (tenant JWT alone = Owner)
  staffToken: string | null;
  staffId: number | null;
  staffRole: string | null;
  staffName: string | null;
  // Billing / account gate state
  billingStatus: string | null;
  trialEndsOn: string | null;
  subscriptionRenewalOn: string | null;
  accountSuspended: boolean;
  tenantTimezone: string;
  _hasHydrated: boolean;

  setAuth: (token: string, tenantSlug: string, sessionId: string, timezone?: string) => void;
  setClientType: (clientType: ClientType) => void;
  clearAuth: () => void;
  setStaffSession: (token: string | null, staffId: number | null, role: string, name: string) => void;
  clearStaffSession: () => void;
  setBillingInfo: (billingStatus: string | null, trialEndsOn: string | null, subscriptionRenewalOn: string | null) => void;
  setAccountSuspended: (suspended: boolean) => void;
  setTenantTimezone: (timezone: string) => void;
  setHasHydrated: (v: boolean) => void;
}

// Safe localStorage wrapper that handles errors gracefully
const safeStorage = createJSONStorage(() => ({
  getItem: (name: string) => {
    try {
      return localStorage.getItem(name);
    } catch (e) {
      console.warn(`[authStore] Failed to read localStorage key "${name}":`, e);
      return null;
    }
  },
  setItem: (name: string, value: string) => {
    try {
      localStorage.setItem(name, value);
    } catch (e) {
      console.warn(`[authStore] Failed to write localStorage key "${name}":`, e);
    }
  },
  removeItem: (name: string) => {
    try {
      localStorage.removeItem(name);
    } catch (e) {
      console.warn(`[authStore] Failed to remove localStorage key "${name}":`, e);
    }
  },
}));

export function getRememberedWorkspace(): string | null {
  try { return localStorage.getItem('wl.workspace'); } catch { return null; }
}
export function clearRememberedWorkspace(): void {
  try { localStorage.removeItem('wl.workspace'); } catch { /* ignore */ }
}

export interface RememberedTechnicianSession { token: string; tenantSlug: string; staffId: number; staffRole: string; staffName: string; expiresAt: string; }
const TECHNICIAN_SESSION_KEY = 'wl.technician.session';
export function rememberTechnicianSession(session: RememberedTechnicianSession): void { try { sessionStorage.setItem(TECHNICIAN_SESSION_KEY, JSON.stringify(session)); } catch { /* unavailable */ } }
export function getRememberedTechnicianSession(): RememberedTechnicianSession | null {
  try {
    const raw = sessionStorage.getItem(TECHNICIAN_SESSION_KEY); if (!raw) return null;
    const session = JSON.parse(raw) as RememberedTechnicianSession;
    if (!session.token || !session.tenantSlug || !session.staffRole || new Date(session.expiresAt).getTime() <= Date.now()) { sessionStorage.removeItem(TECHNICIAN_SESSION_KEY); return null; }
    return session;
  } catch { return null; }
}
export function clearRememberedTechnicianSession(): void { try { sessionStorage.removeItem(TECHNICIAN_SESSION_KEY); } catch { /* unavailable */ } }

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      tenantSlug: null,
      sessionId: null,
      clientType: (import.meta.env.VITE_CLIENT_TYPE as ClientType) ?? 'Web',
      staffToken: null,
      staffId: null,
      staffRole: null,
      staffName: null,
      billingStatus: null,
      trialEndsOn: null,
      subscriptionRenewalOn: null,
      accountSuspended: false,
      tenantTimezone: 'America/Toronto',
      _hasHydrated: false,

      setAuth: (token, tenantSlug, sessionId, timezone) =>
        set({ token, tenantSlug, sessionId, tenantTimezone: timezone ?? 'America/Toronto' }),

      setClientType: (clientType) => set({ clientType }),

      clearAuth: () =>
        set({
          token: null,
          tenantSlug: null,
          sessionId: null,
          staffToken: null,
          staffId: null,
          staffRole: null,
          staffName: null,
          billingStatus: null,
          trialEndsOn: null,
          subscriptionRenewalOn: null,
          accountSuspended: false,
          tenantTimezone: 'America/Toronto',
        }),

      setStaffSession: (token, staffId, role, name) =>
        set({ staffToken: token, staffId, staffRole: role, staffName: name }),

      clearStaffSession: () =>
        set({ staffToken: null, staffId: null, staffRole: null, staffName: null }),

      setBillingInfo: (billingStatus, trialEndsOn, subscriptionRenewalOn) =>
        set({ billingStatus, trialEndsOn, subscriptionRenewalOn }),

      setAccountSuspended: (suspended) => set({ accountSuspended: suspended }),

      setTenantTimezone: (timezone) => set({ tenantTimezone: timezone }),

      setHasHydrated: (v) => set({ _hasHydrated: v }),
    }),
    {
      name: 'wl-auth',
      storage: safeStorage,
      partialize: ({ _hasHydrated, setHasHydrated, ...rest }) => rest,
      onRehydrateStorage: () => (state) => { state?.setHasHydrated(true); },
    }
  )
);
